import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, Building2, Phone, Mail, Search, Trash2, Edit3, UserCircle2 } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  "医療機関",
  "病院",
  "クリニック",
  "ケアマネジャー",
  "居宅介護支援事業所",
  "地域包括支援センター",
  "介護事業所",
  "行政",
  "その他",
];

const KIND_COLORS = {
  "医療機関": "#4A6B8C",
  "病院": "#4A6B8C",
  "クリニック": "#3A6B52",
  "ケアマネジャー": "#2C5545",
  "居宅介護支援事業所": "#2C5545",
  "地域包括支援センター": "#D4A373",
  "介護事業所": "#8B6424",
  "行政": "#8A8A86",
  "その他": "#4A4A48",
};

const EMPTY = { kind: "医療機関", name: "", person_in_charge: "", postal_code: "", address: "", phone: "", fax: "", email: "", note: "" };

export default function Referrers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    api.get("/referrers", { params: { kind: kindFilter || undefined, q: q || undefined } })
      .then((r) => setItems(r.data))
      .catch(() => {});
  };
  useEffect(load, [kindFilter, q]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const save = async () => {
    if (!form.name) { toast.error("名称は必須です"); return; }
    try {
      if (editing) {
        await api.patch(`/referrers/${editing.id}`, form);
        toast.success("医療機関を更新しました");
      } else {
        await api.post("/referrers", form);
        toast.success("医療機関を登録しました");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "保存失敗");
    }
  };

  const del = async (id) => {
    if (!confirm("この医療機関を削除しますか?")) return;
    try {
      await api.delete(`/referrers/${id}`);
      toast.success("削除しました");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "削除失敗");
    }
  };

  // Group by kind
  const groups = {};
  items.forEach((r) => { (groups[r.kind] ||= []).push(r); });

  return (
    <div className="space-y-6" data-testid="referrers-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#8A8A86] font-numeric mb-2">REFERRERS</div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">医療機関管理</h1>
          <p className="text-[#4A4A48] mt-1">医療機関・ケアマネジャー・地域包括支援センター等のマスタ管理 ({items.length}件)</p>
        </div>
        <Button data-testid="new-referrer-button" onClick={openNew} className="bg-[#2C5545] hover:bg-[#244538] text-white">
          <Plus size={16} className="mr-2" />新規登録
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A86]" />
          <Input
            data-testid="referrer-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="名称・担当者で検索"
            className="pl-10 h-10 border-[#E5E5E3]"
          />
        </div>
        <select
          value={kindFilter}
          onChange={(e) => setKindFilter(e.target.value)}
          className="flex h-10 rounded-md border border-input px-3 text-sm bg-white"
        >
          <option value="">全種別</option>
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 text-center border-[#E5E5E3] bg-white">
          <Building2 size={36} className="mx-auto text-[#8A8A86] mb-3" />
          <div className="text-[#8A8A86]">まだ医療機関が登録されていません</div>
          <Button onClick={openNew} variant="outline" className="mt-4 border-[#2C5545] text-[#2C5545]">
            <Plus size={14} className="mr-1" />最初の医療機関を登録
          </Button>
        </Card>
      ) : (
        <div className="space-y-6">
          {KINDS.filter((k) => groups[k]).map((kind) => (
            <div key={kind}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-1 h-5 rounded" style={{ background: KIND_COLORS[kind] }} />
                <h3 className="font-bold text-[#1C1C1A]">{kind}</h3>
                <Badge variant="outline" className="font-numeric text-xs">{groups[kind].length}件</Badge>
                <div className="flex-1 border-b border-dashed border-[#E5E5E3]" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {groups[kind].map((r) => (
                  <Card key={r.id} className="p-4 border-[#E5E5E3] bg-white hover:shadow-md transition group">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: KIND_COLORS[kind] + "20" }}>
                        <Building2 size={18} style={{ color: KIND_COLORS[kind] }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#1C1C1A] truncate">{r.name}</div>
                        {r.person_in_charge && (
                          <div className="flex items-center gap-1 text-sm text-[#4A4A48] mt-0.5">
                            <UserCircle2 size={12} className="text-[#8A8A86]" />
                            <span>{r.person_in_charge}</span>
                          </div>
                        )}
                        {r.address && (
                          <div className="text-xs text-[#8A8A86] mt-1 truncate">{r.postal_code} {r.address}</div>
                        )}
                        <div className="flex gap-3 mt-2 text-xs text-[#4A4A48] flex-wrap font-numeric">
                          {r.phone && <span className="flex items-center gap-1"><Phone size={10} className="text-[#8A8A86]" />{r.phone}</span>}
                          {r.fax && <span>FAX: {r.fax}</span>}
                          {r.email && <span className="flex items-center gap-1 truncate"><Mail size={10} className="text-[#8A8A86]" />{r.email}</span>}
                        </div>
                        {r.note && <div className="text-xs text-[#8A8A86] mt-2 line-clamp-2">{r.note}</div>}
                      </div>
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition">
                        <button data-testid={`edit-referrer-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-[#F3F3F1] rounded text-[#4A4A48]"><Edit3 size={13} /></button>
                        <button onClick={() => del(r.id)} className="p-1.5 hover:bg-[#FDF5F5] rounded text-[#A74141]"><Trash2 size={13} /></button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "医療機関 編集" : "医療機関 新規登録"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>種別<span className="text-[#A74141]">*</span></Label>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="flex h-10 w-full rounded-md border border-input px-3 text-sm bg-white">
                  {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
              <div><Label>名称<span className="text-[#A74141]">*</span></Label><Input data-testid="ref-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="例:○○病院、○○ケアプランセンター" /></div>
            </div>
            <div><Label>担当者氏名</Label><Input value={form.person_in_charge} onChange={(e) => setForm({ ...form, person_in_charge: e.target.value })} placeholder="例:田中 次郎(ケアマネジャー)" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>郵便番号</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} placeholder="123-4567" /></div>
              <div><Label>電話番号</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            </div>
            <div><Label>住所</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>FAX</Label><Input value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} /></div>
              <div><Label>メール</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div><Label>備考・連携事項</Label><Textarea rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="担当者の専門分野、連絡可能時間、これまでの連携実績など" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>キャンセル</Button>
            <Button data-testid="save-referrer-button" className="bg-[#2C5545] text-white" onClick={save}>
              {editing ? "更新" : "登録"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
